import { useQuery } from '@apollo/client';
import { ErrorFallback, PageHeader, ScheduleListView } from '@components';
import styled from '@emotion/styled';
import { flightsQuery } from '@graphql';
import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
} from '@ionic/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useEffect, useState } from 'react';
import { DATETIME_FORMAT } from '@models';
import { isValid, isBefore, format } from 'date-fns';
import { CrewHubActionTypes } from '@hooks/lib/crew-hub-store/crew-hub-store-actions';
import { useCrewHubStoreDispatch, useFlightsForCrew, useFlightSubscriptions, useManifest, useManifestSubscription, useSeatMap, useSeatMapSubscription } from '@hooks';
import { ScheduleResponse } from '@models/src/lib/schedule-response';
import { environment } from 'environments/environment';
import { convertFlightsToActivities, parseDate } from '@utilities';

const StyledPage = styled(IonPage)(
  ({theme}) => `
    background: ${theme.colors.primary}
  `
);

const SearchForm = styled.form(
  ({theme}) => `
    padding: 1rem;
    background: ${theme.colors.nicest};
    margin-bottom: 1rem;

    // Style all IonItems in the form
    ion-item {
      --background: ${theme.colors.primary};
      --border-color: ${theme.colors.lightBlue};
      --border-width: 1px;
      --border-style: solid;
      --border-radius: 4px;
      --padding-start: 1rem;
      --highlight-height: 2px;
      margin-bottom: 1rem;

      &.item-has-focus {
        --border-color: ${theme.colors.lightBlue};
        --highlight-background: ${theme.colors.lightBlue};
      }
    }

    // Style all IonInputs in the form
    ion-input {
      --background: ${theme.colors.primary};
      --color: ${theme.colors.white};
      --placeholder-color: ${theme.colors.accentGrey};
      --placeholder-opacity: 0.7;
    }

    // Style all IonLabels in the form
    ion-label {
      --color: ${theme.colors.lightBlue} !important;
      font-weight: 500;
    }

    // Style the submit button
    ion-button {
      --background: ${theme.colors.lightBlue};
      --background-hover: ${theme.colors.darkBlue};
      --color: ${theme.colors.white};
      margin-top: 1rem;
      font-weight: 500;
    }
  `
);

const ErrorText = styled.div(
  ({theme}) => `
    color: ${theme.colors.lightRed};
    font-size: 0.8rem;
    margin: 0.2rem 0 0 1rem;
  `
);

export function FlightsPage() {
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [departureAirport, setDepartureAirport] = useState<string>('');
  const [arrivalAirport, setArrivalAirport] = useState<string>('');
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [errors, setErrors] = useState<{
    fromDate?: string;
    toDate?: string;
    dates?: string;
    departureAirport?: string;
    arrivalAirport?: string;
  }>({});
  const dispatch = useCrewHubStoreDispatch();
  const navitaireBaseUrl = environment.navitaireUrl
  const apiScope = environment.apiScope;

  const formatDateInput = (value: string, currentValue: string): string => {
    // Remove any non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Don't process if deleting
    if (value.length < currentValue.length) {
      return value;
    }
    
    // Add slashes automatically
    if (numbers.length >= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    } else if (numbers.length >= 2) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    }
    
    return numbers;
  };

  const validateDate = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const parsed = parseDate(dateStr);
    return isValid(parsed);
  };

  const validateDates = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!fromDate) {
      newErrors.fromDate = 'From date is required';
    } else if (!validateDate(fromDate)) {
      newErrors.fromDate = 'Invalid date format';
    }

    if (!toDate) {
      newErrors.toDate = 'To date is required';
    } else if (!validateDate(toDate)) {
      newErrors.toDate = 'Invalid date format';
    }

    if (validateDate(fromDate) && validateDate(toDate)) {
      const parsedFrom = parseDate(fromDate);
      const parsedTo = parseDate(toDate);
      
      if (!isBefore(parsedFrom, parsedTo)) {
        newErrors.dates = 'From date must be before To date';
      }
    }

    if (departureAirport.length !== 3) {
      newErrors.departureAirport = 'Departure airport must be 3 characters';
    }

    if (arrivalAirport.length !== 3) {
      newErrors.arrivalAirport = 'Arrival airport must be 3 characters';
    }

    if (departureAirport === arrivalAirport) {
      newErrors.departureAirport = 'Departure and arrival airports cannot be the same';
      newErrors.arrivalAirport = 'Departure and arrival airports cannot be the same';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (setValue: (value: string) => void, value: string) => {
    setSearchEnabled(false);
    setValue(value);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateDates() && departureAirport.length === 3 && arrivalAirport.length === 3) {
      setSearchEnabled(true);
    }
  };

  const { data, loading, error } = useQuery(flightsQuery, {
    variables: { 
      from: fromDate && validateDate(fromDate) ? parseDate(fromDate) : '',
      to: toDate && validateDate(toDate) ? parseDate(toDate) : '',
      departureAirport: departureAirport,
      arrivalAirport: arrivalAirport
    },
    skip: !searchEnabled,
    fetchPolicy: 'network-only'
  });

  const timeStamp = () => format(new Date(), DATETIME_FORMAT);

  useEffect(() => {
    if (data?.flights?.flights) {
      const responseToStore = {
        responseCode: error ? '!200' : '200',
        errorMessage: error?.message || '',
        time: timeStamp(),
        schedule: convertFlightsToActivities(data.flights.flights),
      }
      dispatch({
        type: CrewHubActionTypes.setScheduleResponse,
        payload: responseToStore as ScheduleResponse,
      });
    }
  },[data])

  const {loading: flightsLoading, error: flightsError} =  useFlightsForCrew();
  const {loading: manifestLoading, error: manifestError} = useManifest(navitaireBaseUrl, apiScope);
  const {loading: seatMapLoading, error: seatMapError} = useSeatMap(navitaireBaseUrl, apiScope);

  useFlightSubscriptions();
  useManifestSubscription();
  useSeatMapSubscription();

  return (
    <StyledPage>
      <PageHeader
        showFlightNumber={false}
        showLogo={true}
        loadingMessage={'Flights Loading...'}
        useModal={true}
        scheduleError={error}
        flightsError={flightsError}
        manifestsError={manifestError}
        seatMapsError={seatMapError}
      />
      <IonContent fullscreen>
        <IonRefresher slot="fixed">
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <SearchForm onSubmit={handleSearch}>
            <IonItem>
              <IonLabel position="stacked">From Date (MM/DD/YYYY)</IonLabel>
              <IonInput
                value={fromDate}
                onIonChange={e => handleInputChange(setFromDate, formatDateInput(e.detail.value || '', fromDate))}
                placeholder="MM/DD/YYYY"
                maxlength={10}
                type="tel"
              />
              {errors.fromDate && <ErrorText>{errors.fromDate}</ErrorText>}
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">To Date (MM/DD/YYYY)</IonLabel>
              <IonInput
                value={toDate}
                onIonChange={e => handleInputChange(setToDate, formatDateInput(e.detail.value || '', toDate))}
                placeholder="MM/DD/YYYY"
                maxlength={10}
                type="tel"
              />
              {errors.toDate && <ErrorText>{errors.toDate}</ErrorText>}
            </IonItem>
            {errors.dates && <ErrorText>{errors.dates}</ErrorText>}
            
            <IonItem>
              <IonLabel position="stacked">Departure Airport</IonLabel>
              <IonInput
                value={departureAirport}
                onIonChange={e => handleInputChange(setDepartureAirport, e.detail.value?.toUpperCase() || '')}
                placeholder="Enter airport code"
                maxlength={3}
              />
              {errors.departureAirport && <ErrorText>{errors.departureAirport}</ErrorText>}
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Arrival Airport</IonLabel>
              <IonInput
                value={arrivalAirport}
                onIonChange={e => handleInputChange(setArrivalAirport, e.detail.value?.toUpperCase() || '')}
                placeholder="Enter airport code"
                maxlength={3}
              />
              {errors.arrivalAirport && <ErrorText>{errors.arrivalAirport}</ErrorText>}
            </IonItem>
            <IonButton expand="block" type="submit" disabled={loading || flightsLoading || manifestLoading || seatMapLoading}>
              {loading || flightsLoading || manifestLoading || seatMapLoading ? 'Loading...' : 'Search Flights'}
            </IonButton>
          </SearchForm>

          {data?.flights?.flights && (
            <ScheduleListView
              flights={data?.flights?.flights}
              loading={loading}
              error={error}
            />
          )}
        </ErrorBoundary>
      </IonContent>
    </StyledPage>
  );
}
